import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import { closeLoan, loadLoans, requestLoan, type LoanRow } from "../data";
import { money } from "../lib/payslip";
import { loanTone } from "../pages/Loans";
import type { Workspace } from "../types";

/** Self-service: ask for a loan or salary advance and follow what is left to repay. */
export function MyLoans({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const cid = ws.membership.company_id;
  const [rows, setRows] = useState<LoanRow[]>([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: "advance" as "loan" | "advance", amount: "", n: "1", reason: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => loadLoans(cid).then((r) => setRows(r.filter((x) => x.employment_id === ws.membership.employment_id))).catch(() => setRows([]));
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const waiting = rows.find((r) => r.status === "requested");

  async function send(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try { await requestLoan(cid, f.kind, Number(f.amount), Number(f.n), f.reason); setOpen(false); setMsg({ ok: true, text: t("requestSent") }); await refresh(); }
    catch (err) { setMsg({ ok: false, text: err instanceof Error ? err.message : t("errorGeneric") }); }
  }

  return (
    <section className="card">
      <div className="row spread">
        <h2>{t("myLoans")}</h2>
        {!open && !waiting && <button type="button" className="btn secondary small" onClick={() => setOpen(true)}>{t("loanAsk")}</button>}
      </div>
      {open && (
        <form className="stack" onSubmit={send}>
          <div className="field-row">
            <label className="field"><span>{t("otKind")}</span>
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "loan" | "advance" })}>
                <option value="advance">{t("loanKind_advance")}</option><option value="loan">{t("loanKind_loan")}</option>
              </select></label>
            <label className="field"><span>{t("payAmount")}</span><input type="number" min="1" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required /></label>
            <label className="field"><span>{t("loanInstallments")}</span><input type="number" min="1" max="60" value={f.n} onChange={(e) => setF({ ...f, n: e.target.value })} required /></label>
          </div>
          <label className="field"><span>{t("loanReason")}</span><input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></label>
          <p className="muted small-note">{t("loanRule")}</p>
          <div className="actions">
            <button type="button" className="btn secondary" onClick={() => setOpen(false)}>{t("cancel")}</button>
            <button className="btn primary">{t("sendToHr")}</button>
          </div>
        </form>
      )}
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {rows.length === 0 ? !open && <p className="muted">{t("loanNoneMine")}</p> : (
        <ul className="req-list">
          {rows.map((r) => (
            <li key={r.loan_id}>
              <span><strong>{t(`loanKind_${r.kind}` as StringKey)}</strong> · <span className="mono">{money(r.principal)}</span>
                {r.status === "active" && <span className="muted small-note"> · {t("loanLeft")} <span className="mono">{money(r.outstanding)}</span>{r.next_due ? ` · ${t("loanNext")} ${r.next_due.slice(0, 7)}` : ""}</span>}</span>
              <span className="row">
                <span className={`badge ${loanTone[r.status]}`}>{t(`loanStatus_${r.status}` as StringKey)}</span>
                {r.status === "requested" && <button type="button" className="btn secondary small" onClick={async () => { await closeLoan(r.loan_id, ""); await refresh(); }}>{t("cancelRequest")}</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
